import React, { useState } from 'react';
import { Layers } from 'lucide-react';
import FileUpload from '../components/common/FileUpload';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import TaskProgressPanel from '../components/conversion/TaskProgressPanel';
import ConversionHistory from '../components/conversion/ConversionHistory';
import { useConversionProcess } from '../hooks/useConversionProcess';

const MergePDF: React.FC = () => {
    const [files, setFiles] = useState<File[]>([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const {
        taskId,
        isCreating,
        processError,
        setProcessError,
        executeTask,
        resetTask,
    } = useConversionProcess();

    const handleFilesSelected = (selectedFiles: File[]) => {
        setFiles(selectedFiles);
        setProcessError(null);
    };

    const handleMerge = async () => {
        if (files.length < 2) {
            setProcessError('请至少选择 2 个 PDF 文件进行合并');
            return;
        }

        await executeTask(files, 'merge');
    };

    const handleReset = () => {
        resetTask();
        setFiles([]);
    };

    return (
        <div className="merge-page">
            <div className="container">
                <div className="page-header">
                    <h1>合并 PDF 文件</h1>
                    <p>将多个 PDF 文件合并为一个文件</p>
                </div>

                <div className="page-content">
                    <Card glass>
                        {!taskId ? (
                            <>
                                <div className="upload-section">
                                    <FileUpload
                                        onFilesSelected={handleFilesSelected}
                                        multiple={true}
                                        maxFiles={20}
                                    />
                                </div>

                                {files.length > 0 && (
                                    <div className="action-section">
                                        <div className="file-count">
                                            已选择 <strong>{files.length}</strong> 个文件
                                        </div>

                                        <Button
                                            variant="primary"
                                            size="lg"
                                            onClick={handleMerge}
                                            isLoading={isCreating}
                                            icon={<Layers />}
                                            disabled={files.length < 2 || isCreating}
                                        >
                                            {isCreating ? '准备合并...' : '开始合并'}
                                        </Button>
                                    </div>
                                )}

                                {processError && !taskId && (
                                    <div className="message message-error" style={{ marginTop: '15px' }}>
                                        {processError}
                                    </div>
                                )}
                            </>
                        ) : (
                            <TaskProgressPanel taskId={taskId} onReset={handleReset} onComplete={() => setRefreshKey(prev => prev + 1)} />
                        )}
                    </Card>

                    <div className="tips-section">
                        <Card>
                            <h3>💡 使用提示</h3>
                            <ul>
                                <li>支持同时上传多个 PDF 文件</li>
                                <li>文件将按照上传顺序进行合并</li>
                                <li>单个文件最大支持 50MB</li>
                                <li>合并后的文件大时，处理可能会需要一段时间，请耐心等待</li>
                            </ul>
                        </Card>
                    </div>

                    <ConversionHistory operationFilter="merge" onRefreshTrigger={refreshKey} />
                </div>
            </div>
        </div>
    );
};

export default MergePDF;
